import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'

import { ProBadge } from '@/components/atoms/pro-badge.atom'
import { Button } from '@/components/ui/button'
import { TabsTrigger } from '@/components/ui/tabs'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

interface AgencyTabItemProps {
  value: string
  label: string
  icon: LucideIcon
  isDisabled?: boolean
  onUpgradeClick?: () => void
}

/**
 * A single tab trigger for the agencies page header.
 * When disabled, shows a Pro badge and an upgrade tooltip on hover.
 */
export const AgencyTabItem = ({
  value,
  label,
  icon: Icon,
  isDisabled = false,
  onUpgradeClick,
}: AgencyTabItemProps): ReactNode => {
  const trigger = (
    <TabsTrigger
      value={value}
      disabled={isDisabled}
      className="agencies-tab-trigger relative gap-2 px-4"
    >
      <Icon className="size-4" />
      {label}
      {isDisabled && <ProBadge />}
    </TabsTrigger>
  )

  if (!isDisabled) {
    return trigger
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex">{trigger}</span>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="flex flex-col items-center gap-2 p-2">
        <span>Available on Pro plan</span>
        {onUpgradeClick && (
          <Button size="sm" className="upgrade-button" onClick={onUpgradeClick}>
            Upgrade
          </Button>
        )}
      </TooltipContent>
    </Tooltip>
  )
}
